import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  styled,
  Snackbar,
  Alert,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { compareProductAccordionSections } from '../../shared/shared.constants';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { setCompareData } from '../../store/slices/metaDataSlice';

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '0 1rem 0 0',
  maxHeight: '200px',
  overflowY: 'auto',
  scrollbarGutter: 'stable', // Reserve space for scrollbar
  '&::-webkit-scrollbar': {
    width: '0.375rem'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: '0.1875rem'
  }
}));

const ProductItem = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem 1rem',
  borderTop: `0.0625rem solid ${theme.palette.grey[300]}`,
  borderBottom: `0.0625rem solid ${theme.palette.grey[300]}`,
  '&:not(:first-of-type)': {
    borderTop: 'none'
  }
}));

const MAX_SELECTIONS = 2;

const ProductSelector: React.FC = () => {
  const dispatch = useDispatch();
  const [showAlert, setShowAlert] = useState(false);

  const { compare } = useAppSelector(metaDataSelector);
  const { checkedItems } = compare;

  const selectedCount = Object.values(checkedItems).filter(Boolean).length;

  const handleCheckboxChange = (id: number) => {
    if (!checkedItems[id] && selectedCount >= MAX_SELECTIONS) {
      setShowAlert(true);
      return;
    }

    dispatch(
      setCompareData({
        ...compare,
        checkedItems: {
          ...compare.checkedItems,
          [id]: !compare.checkedItems[id]
        }
      })
    );
  };

  return (
    <div
      style={{
        width: '100%',
        margin: '1.25rem auto',
        paddingRight: '0.375rem' // Compensate for scrollbar space
      }}
    >
      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" sx={{ width: '100%' }}>
          Maximum {MAX_SELECTIONS} products can be selected for comparison
        </Alert>
      </Snackbar>
      {compareProductAccordionSections.map((section) => (
        <Accordion
          key={section.title}
          sx={{
            width: '100%',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: '0.0625rem solid #e0e0e0',
            marginBottom: '0.5rem'
          }}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              {section.title}
            </Typography>
          </AccordionSummary>

          <CustomAccordionDetails>
            {section.products.map((product) => (
              <ProductItem key={product.id}>
                <div>
                  <Typography variant="body1">{product.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {product.volume}
                  </Typography>
                </div>
                <Checkbox
                  checked={!!checkedItems[product.id]}
                  onChange={() => handleCheckboxChange(product.id)}
                  sx={{ padding: 0 }}
                />
              </ProductItem>
            ))}
          </CustomAccordionDetails>
        </Accordion>
      ))}

      {/* {selectedProducts.length === 2 && renderComparisonTable()} */}
    </div>
  );
};

export { ProductSelector };
